import { makeStyles } from '~/UIKit/Style';
import { Form, Formik } from 'formik';
import React, { useState } from 'react';
import { ObjectSchema } from 'yup';
import { IItemCreationHeader, ItemCreationSection } from '.';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { Error } from '../Error';
import { ItemCreationToolbar } from './ItemCreationToolbar';
import { FormErrors } from '~/Components/FormErrors';
import { isEqual } from 'lodash-es';
import { useNavigationContext } from '~/Hooks';

export interface IItemCreationProps<T> {
  onSave: () => void;
  validate: (values: T) => void;
  headers: IItemCreationHeader[];
  listItems: ItemCreationSection<T>[];
  initialValues: T;
  networkError: IErrorResponse | undefined;
  isLoading: boolean;
  skipConfirmationModal?: boolean;
  validationSchema?: ObjectSchema<Partial<T>>;
  onValidationErrors?: (hasErrors: boolean) => void;
  showFormErrors?: boolean;
  validateOnBlur?: boolean;
  disableMobile?: boolean;
  validateOnMount?: boolean;
}

const useStyles = makeStyles(() => ({
  itemCreationContainer: { flex: 1 },
}));

export const ItemCreation = <T extends object>({
  onSave,
  headers,
  listItems,
  initialValues,
  validate,
  skipConfirmationModal = false,
  networkError,
  isLoading,
  validationSchema,
  onValidationErrors,
  showFormErrors = false,
  validateOnBlur = true,
  disableMobile = false,
  validateOnMount = false,
}: IItemCreationProps<T>) => {
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const ListItem = listItems[activeSectionIndex];
  const { classes } = useStyles();
  const { blockNavigation } = useNavigationContext();

  return (
    <div className={classes.itemCreationContainer}>
      {headers.length > 1 && (
        <ItemCreationToolbar
          activeSectionIndex={activeSectionIndex}
          setActiveSectionIndex={setActiveSectionIndex}
          headers={headers}
          isLoading={isLoading}
          disableMobile={disableMobile}
        />
      )}
      <Error error={networkError} />

      <Formik
        initialValues={initialValues}
        onSubmit={onSave}
        validate={validate}
        validationSchema={validationSchema}
        validateOnBlur={validateOnBlur}
        validateOnMount={validateOnMount}
        enableReinitialize
      >
        {(formik) => {
          onValidationErrors?.(!formik.isValid);
          if (formik.isSubmitting !== isLoading) {
            formik.setSubmitting(isLoading);
          }
          if (!isLoading && !skipConfirmationModal) {
            blockNavigation(!isEqual(formik.values, initialValues));
          }
          return (
            <>
              {showFormErrors && <FormErrors errors={formik.errors} />}
              <Form>
                <ListItem
                  data={formik.values || ({} as any)}
                  setFieldValue={formik.setFieldValue}
                />
              </Form>
            </>
          );
        }}
      </Formik>
    </div>
  );
};
